import { ABTestVariantInfo } from '../insights/types';
import { WorkflowVersionType } from 'src/slices/workflow-preview/types';
import { SolveWidgetProduct } from 'src/types/types';

export interface CreateOrUpdateSolveABTestConfig {
  description?: string | null;
  /** Condition for ending the test */
  end_condition?: EndCondition | null;
  intent_workflow_id?: string | null;
  /** Name of the AB test */
  name: string;
  product?: SolveWidgetProduct | null;
  /** When the test should start */
  start_datetime?: string | null;
  summary?: string | null;
  /** Timezone for start/end times */
  timezone?: string;
  /**
   * Percentage of total traffic routed to this A/B test
   * Must be between 0.0 and 100.0
   */
  total_traffic_percentage?: number | null;
  variants?: SolveABTestVariant[] | null;
  /** Metrics used for determining the winner */
  winner_metrics?: WinnerMetric[] | null;
}

export interface SolveABTestVariant {
  /** Version number of the intent workflow */
  intent_workflow_version: number;
  /** Whether this is the control variant */
  is_control: boolean;
  /** Optional name for the variant */
  name: string | null;
  /** Percentage of traffic routed to this variant. Must be between 0.0 and 100.0 */
  traffic_percentage: number;
  /** Type of the workflow version (auto_saved, saved, published) */
  version_type: WorkflowVersionType;
}

export type EndConditionType = 'fixed_duration' | 'sample_count';

export interface EndCondition {
  condition_type: EndConditionType;
  end_datetime?: string | null;
  sample_count?: number | null;
}

export interface WinnerMetric {
  metric_id: string;
  metric_type: string;
}

export interface AvailableWinnerMetric {
  display_name: string;
  metric_id: string;
  metric_type: string;
}

export interface ListAvailableWinnerMetrics {
  predefined_metrics: AvailableWinnerMetric[];
}

export const MAX_WINNER_METRICS = 3;

export const VARIANT_DEFAULT_NAMES = ['Control', 'Variant A', 'Variant B'];

export type SolveABTestStatus = 'draft' | 'running' | 'completed' | 'canceled';

/**
 * Response model for Solve A/B Test Config
 */
export interface SolveABTestConfig {
  ab_test_config_id: string;
  created_date: string;
  description?: string | null;
  end_condition?: EndCondition | null;
  intent_workflow_id?: string | null;
  last_modified_by: string;
  modified_date: string;
  name: string;
  org_id: number;
  product: SolveWidgetProduct;
  start_datetime?: string | null;
  status: SolveABTestStatus;
  summary?: string | null;
  timezone?: string | null;
  total_traffic_percentage?: number | null;
  variants?: SolveABTestVariant[] | null;
  winner_metrics?: WinnerMetric[] | null;
}

/**
 * Pagination metadata for list responses
 */
export interface PaginationMetadata {
  page: number;
  page_size: number;
  total: number;
}

/**
 * Response model for listing A/B test configurations
 */
export interface ListSolveABTestConfigs {
  ab_test_configs: SolveABTestConfig[];
  metadata: PaginationMetadata;
}

export interface VariantSummary {
  variant_name?: string | null;
  sample_count: number;
  traffic_percentage: number;
}

/**
 * Summary model for A/B test configuration
 * Enhanced for calendar view with start/end dates, traffic allocation, and product info.
 */
export interface SolveABTestConfigSummary {
  ab_test_config_id: string;
  description?: string | null;
  end_condition?: EndCondition | null;
  end_datetime?: string | null;
  intent_name?: string | null;
  name: string;
  product: SolveWidgetProduct;
  start_datetime?: string | null;
  status: SolveABTestStatus;
  timezone: string;
  /** Only for running/completed tests */
  total_sample_count?: number | null;
  total_traffic_percentage: number;
  /** Only for running/completed tests */
  variant_summary?: VariantSummary[] | null;
}

/**
 * Response model for listing A/B test configurations with summary
 */
export interface ListSolveABTestConfigsSummary {
  ab_test_configs: SolveABTestConfigSummary[];
  metadata: PaginationMetadata;
}

/**
 * Request model for renaming an A/B test configuration
 */
export interface RenameSolveABTestConfig {
  description: string;
  name: string;
}

export interface VersionFreshnessIssue {
  variant_name: string;
  variant_version: number;
  variant_version_type: string;
  is_control: boolean;
  issue: 'control_not_live' | 'non_control_is_live' | 'no_live_version';
  current_live_version: number | null;
  current_live_version_type: string | null;
  message: string;
}

export interface ValidateAbTestConfigResponse {
  is_valid: boolean;
  validation_errors: string[];
  ab_test_config_id: string;
  version_freshness_issues?: VersionFreshnessIssue[];
}

export interface WinnerResponse {
  winner: Winner | null; // Null if draw
  variants: Variant[]; // Ordered as [Winner, Loser 1, Loser 2]
}

interface Winner {
  name: string; // 'Control' | 'Variant A' | 'Variant B'
  intent_workflow_id: string;
  intent_workflow_version: number;
}

interface Variant {
  name: string; // 'Control' | 'Variant A' | 'Variant B'
  version_name: string; // e. g. 'Version 2' or 'Jul 21, 2025, 3:14 PM'
  is_control: boolean;
  metrics: Metric[];
}

interface Metric {
  name: string; // e. g. 'Deflection'
  value: number; // e. g. 23 or -42
  suffix: string; // e. g. '%'
}

export interface ABTestAIRecommendationResponse {
  generated_at: string;
  key_learnings: string[];
  recommendation: string | null;
}

export interface WorkflowABTestVariantsResponse {
  data: Record<string, ABTestVariantInfo[]>;
}

// --- Variant Summary Types ---

export interface GenerateVariantSummaryRequest {
  intent_workflow_id: string;
  intent_workflow_version: number;
  variant_name: string;
  version_type: WorkflowVersionType;
}

export interface GenerateVariantSummaryResponse {
  intent_workflow_version: number;
  summary: string | null;
  variant_name: string;
  version_type: WorkflowVersionType;
}

// --- Recommendations Types ---

export type RecommendationSettingKey =
  | 'winner_metric'
  | 'end_condition_type'
  | 'duration_days'
  | 'sample_count';

export interface ABTestRecommendation {
  description: string;
  setting_key: RecommendationSettingKey;
  setting_value: unknown;
  title: string;
}
